import {
  Component,
  HostListener,
  OnInit,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef
} from '@angular/core';
import {
  SuperHttpClientProvider,
  HttpCriteria
} from '@app/core/http';
const Highcharts: any = require('highcharts/highstock');
import { options as baseOptions } from './highcharts/highcharts-options';
import { SensorDataService } from '@app/core/sensor-data.service';
import { SensorService } from '@app/core/sensor.service';
import { SensorMoistureZonesService } from '@app/core/sensor-moisture-zones.service';
import { SensorMoistureProfileService } from '@app/core/sensor-moisture-profile.service';
import { MoistureProfileAdminService } from '@app/core/moisture-profile-admin.service';
import { forkJoin, of, Subscription, Observable } from 'rxjs';
import { SpinnerService } from '@app/shared/spinner';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

type ChartKey = 'chart1' | 'chart2';
type SensorType =
  | 'FL'
  | 'HU'
  | 'SH'
  | 'SC'
  | 'HH'
  | 'TE'
  | 'WC'
  | 'DE'
  | 'CO'
  | 'AN'
  | 'BA'
  | 'PL'
  | 'ED'
  | 'DIGITAL'
  | 'E8';

interface SensorGraphSelection {
  idSensor: number;
  sensorType: SensorType;
  moduleId: number;
  moduleName: string;
  fincaId: number;
  fincaName: string;
  clientId?: number | null;
  value: number;
}

interface ModuleSelectionState {
  moduleId: number;
  moduleName: string;
  fincaId: number;
  fincaName: string;
  clientId?: number | null;
  selections: SensorGraphSelection[];
}

interface SeriesMeta {
  nameKey: string;
  group: string;
  type?: string;
  suffix?: string | null;
  decimals?: number | null;
  applyZones?: boolean;
  unit?: string | null;
}

type SensorRequestResult = {
  selection: SensorGraphSelection;
  data: any;
  zonesMap: Map<number, MoistureZonePeriod[]>;
};

interface MoistureZonePeriod {
  startMs: number;
  endMs: number;
  priority: number;
  id: number;
  zones: Array<{ value?: number; color?: string }>;
}

const DEFAULT_HU_CONTROL_ZONES = [
  { value: 0, color: '#d32f2f' },
  { value: 50, color: '#f57c00' },
  { value: 75, color: '#fbc02d' },
  { value: 100, color: '#2e7d32' },
  { color: '#1565c0' }
];

declare const require: any;
const Boost = require('highcharts/modules/boost');
const NoDataToDisplay = require('highcharts/modules/no-data-to-display');
const HighchartsMore = require('highcharts/highcharts-more');
const Exporting = require('highcharts/modules/exporting');
const ExportData = require('highcharts/modules/export-data');
const MapModule = require('highcharts/modules/map');
const Annotations = require('highcharts/modules/annotations');
const FullScreen = require('highcharts/modules/full-screen');
const OfflineExporting = require('highcharts/modules/offline-exporting');
const WindBarb = require('highcharts/modules/windbarb');

const loadModule = (mod: any) => {
  const fn = mod && mod.default ? mod.default : mod;
  if (typeof fn === 'function') {
    fn(Highcharts);
  }
};

const ensureHighchartsModules = () => {
  loadModule(Boost);
  loadModule(NoDataToDisplay);
  loadModule(HighchartsMore);
  loadModule(Exporting);
  loadModule(ExportData);
  loadModule(MapModule);
  loadModule(Annotations);
  loadModule(FullScreen);
  loadModule(OfflineExporting);
  loadModule(WindBarb);

  if (typeof window !== 'undefined') {
    (window as any).Highcharts = Highcharts;
  }
};

@Component({
  standalone: false,
  selector: 'app-statics',
  templateUrl: './statics.component.html',
  styleUrls: ['./statics.component.scss']
})
export class StaticsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modalStatics', { static: true }) modalStatics: TemplateRef<any>;
  @ViewChild('chart1Container') chart1Container?: ElementRef<HTMLDivElement>;
  @ViewChild('chart2Container') chart2Container?: ElementRef<HTMLDivElement>;
  @ViewChild('mainGraficas') mainGraficas?: ElementRef<HTMLDivElement>;

  fincas: any[] = [];
  finca = -1;
  modulos: any[] = [];
  modulo = -1;
  sensores: any[] = [];
  sensorNames: string[] = [];
  sensorGraphs: SensorGraphSelection[] = [];

  dateRange: { start: Date | null; end: Date | null };

  sensorTypes: any[] = [];
  sensorHumedadActivado = false;
  hasEnviropro8 = false;
  hasSoilHumidity = false;
  showHumSettings = false;
  humind: string[] = [];
  humsum: string[] = [];
  conductivitySelected: string[] = [];
  compareLocked = false;

  hasSecondChart = false;
  hasPrimaryChart = false;

  chart1?: Highcharts.Chart;
  chart2?: Highcharts.Chart;

  private dialogRef?: MatDialogRef<any>;
  private statsRequestId = 0;
  private resizeObservers = new Map<ChartKey, ResizeObserver>();
  private resizeRaf: number | null = null;
  private subscriptions = new Subscription();
  private themeChangeHandler = () => this.onThemeChanged();
  private axisState: Record<
    ChartKey,
    Map<string, { id: number; unit: string | null; min: number; max: number }>
  > = {
    chart1: new Map(),
    chart2: new Map()
  };
  private alignRaf: number | null = null;
  private isAligning = false;
  private syncRaf: number | null = null;
  private isSyncing = false;
  private syncIndex: Record<ChartKey, Map<number, Highcharts.Point[]>> = {
    chart1: new Map(),
    chart2: new Map()
  };
  private moduleSelectionStates = new Map<number, ModuleSelectionState>();

  constructor(
    private $http: SuperHttpClientProvider,
    private sensorDataService: SensorDataService,
    private sensorService: SensorService,
    private sensorMoistureZonesService: SensorMoistureZonesService,
    private sensorMoistureProfileService: SensorMoistureProfileService,
    private moistureProfileAdminService: MoistureProfileAdminService,
    private spinner: SpinnerService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 7);
    this.dateRange = { start, end };
  }

  ngOnInit(): void {
    ensureHighchartsModules();
    this.applyGlobalHighchartsOptions();
    this.spinner.show();
    this.registerLanguageListener();
    this.registerThemeListener();
    this.loadSensorTypes();
  }

  ngAfterViewInit(): void {
    this.scheduleResize();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.disconnectResizeObservers();
    if (
      this.resizeRaf !== null &&
      typeof cancelAnimationFrame !== 'undefined'
    ) {
      cancelAnimationFrame(this.resizeRaf);
    }
    this.subscriptions.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('app-theme-change', this.themeChangeHandler);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleResize();
  }

  openStatics(): void {
    this.dialogRef = this.dialog.open(this.modalStatics, {
      width: '90vw',
      maxWidth: '100vw',
      maxHeight: '90vh',
      disableClose: false,
      autoFocus: 'first-tabbable',
      restoreFocus: false
    });
  }

  saveModal(): void {
    this.dialogRef?.close();
    this.loadStatistics();
  }

  toggleCompareLock(): void {
    if (this.compareLocked) {
      this.compareLocked = false;
      this.clearComparisonSelections(true);
    } else {
      this.compareLocked = true;
      this.persistCurrentModuleSelections();
    }
    this.updateHumidityState();
  }

  updateSensorSelection(
    value: number,
    sensorGraph: SensorGraphSelection
  ): void {
    sensorGraph.value = value;
    this.updateHumidityState();
  }

  private updateHumidityState(selectedSensors?: SensorGraphSelection[]): void {
    const selected =
      selectedSensors ??
      this.getActiveSelectionsForUi();
    const humiditySelected = selected.filter(sg =>
      this.isHumiditySensorType(sg.sensorType)
    );
    const depthHumiditySelected = humiditySelected.filter(
      sg => sg.sensorType !== 'SH' && sg.sensorType !== 'SC'
    );
    this.sensorHumedadActivado = humiditySelected.length > 0;
    this.hasEnviropro8 = humiditySelected.some(sg => sg.sensorType === 'E8');
    this.hasSoilHumidity = humiditySelected.some(
      sg => sg.sensorType === 'SH' || sg.sensorType === 'SC'
    );
    this.showHumSettings = depthHumiditySelected.length > 0;
    if (!this.hasEnviropro8) {
      this.conductivitySelected = [];
    }
  }

  private resetHumiditySelections(): void {
    this.showHumSettings = false;
    this.humind = [];
    this.humsum = [];
    this.conductivitySelected = [];
    this.hasSoilHumidity = false;
  }

  private getCurrentFinca(): any {
    return (this.fincas || []).find(
      (item: any) => Number(item?.idFinca) === Number(this.finca)
    );
  }

  private getCurrentModule(): any {
    return (this.modulos || []).find(
      (item: any) => Number(item?.idModule) === Number(this.modulo)
    );
  }

  private extractClientId(finca: any): number | null {
    const idCliente = Number(finca?.idCliente ?? finca?.cliente?.idCliente);
    return Number.isFinite(idCliente) && idCliente > 0 ? idCliente : null;
  }

  private buildCurrentModuleSelectionState(): ModuleSelectionState | null {
    if (
      !Number.isFinite(Number(this.modulo)) ||
      Number(this.modulo) <= 0 ||
      !this.sensorGraphs.length
    ) {
      return null;
    }
    const finca = this.getCurrentFinca();
    const modulo = this.getCurrentModule();
    const clientId = this.extractClientId(finca);
    return {
      moduleId: Number(this.modulo),
      moduleName: modulo?.name || '',
      fincaId: Number(this.finca),
      fincaName: finca?.name || '',
      clientId,
      selections: this.sensorGraphs.map(selection => ({
        ...selection,
        moduleId: Number(this.modulo),
        moduleName: modulo?.name || '',
        fincaId: Number(this.finca),
        fincaName: finca?.name || '',
        clientId
      }))
    };
  }

  private persistCurrentModuleSelections(): void {
    const state = this.buildCurrentModuleSelectionState();
    if (!state) {
      return;
    }
    this.moduleSelectionStates.set(state.moduleId, state);
  }

  private clearComparisonSelections(keepCurrentModule: boolean): void {
    const currentState = keepCurrentModule
      ? this.buildCurrentModuleSelectionState()
      : null;
    this.moduleSelectionStates.clear();
    if (currentState) {
      this.moduleSelectionStates.set(currentState.moduleId, currentState);
    }
  }

  private getCombinedSelectionStates(): ModuleSelectionState[] {
    if (!this.compareLocked) {
      const currentState = this.buildCurrentModuleSelectionState();
      return currentState ? [currentState] : [];
    }

    const states = new Map(this.moduleSelectionStates);
    const currentState = this.buildCurrentModuleSelectionState();
    if (currentState) {
      states.set(currentState.moduleId, currentState);
    }
    return Array.from(states.values());
  }

  private getActiveSelectionsForUi(): SensorGraphSelection[] {
    if (!this.compareLocked) {
      return this.sensorGraphs.filter(sg => sg.value === 1 || sg.value === 2);
    }
    const selections: SensorGraphSelection[] = [];
    for (const state of this.getCombinedSelectionStates()) {
      selections.push(...state.selections);
    }
    return selections.filter(selection => selection.value === 1 || selection.value === 2);
  }

  private getSelectedSensorsForStatistics(): SensorGraphSelection[] {
    const selections: SensorGraphSelection[] = [];
    for (const state of this.getCombinedSelectionStates()) {
      selections.push(...state.selections);
    }
    return selections
      .filter(selection => selection.value === 1 || selection.value === 2)
      .map(selection => ({ ...selection }));
  }

  fincaChange(): void {
    if (this.compareLocked) {
      this.persistCurrentModuleSelections();
    } else {
      this.moduleSelectionStates.clear();
      this.resetHumiditySelections();
    }
    const criteria = new HttpCriteria({
      _filters: [
        {
          field: 'idFinca',
          operator: '=',
          value: this.finca
        }
      ],
      _perPage: -1
    });
    this.$http.default.filter('/admin/module', criteria).subscribe(data => {
      this.modulos = (data || []).filter((modulo: any) =>
        !this.isDeletedEntity(modulo)
      );
      this.modulo = this.modulos.length ? this.modulos[0].idModule : -1;
      if (this.modulo !== -1) {
        this.moduloChange();
      }
    });
  }

  moduloChange(): void {
    if (this.compareLocked) {
      this.persistCurrentModuleSelections();
    } else {
      this.moduleSelectionStates.clear();
      this.resetHumiditySelections();
    }
    const criteria = new HttpCriteria({
      _filters: [
        {
          field: 'idModule',
          operator: '=',
          value: this.modulo
        }
      ],
      _sort: [
        {
          field: 'sensorType',
          type: 'DESC'
        }
      ],
      _perPage: -1
    });
    this.spinner.show();
    this.$http.default.filter('/admin/sensor', criteria).subscribe(
      data => {
        const currentFinca = this.getCurrentFinca();
        const currentModule = this.getCurrentModule();
        const currentClientId = this.extractClientId(currentFinca);
        const storedSelections = this.compareLocked
          ? this.moduleSelectionStates.get(this.modulo)?.selections || []
          : [];
        const storedValues = new Map<number, number>(
          storedSelections.map(selection => [selection.idSensor, selection.value])
        );
        this.sensores = this.setOrder(
          (data || []).filter((sensor: any) => !this.isDeletedEntity(sensor))
        );
        this.sensorNames = [];
        this.sensorGraphs = this.sensores.map((sensor: any) => {
          const name =
            this.sensorTypes.find(t => t.value === sensor.sensorType)?.name ||
            sensor.sensorType;
          this.sensorNames.push(name);
          return {
            idSensor: sensor.idSensor,
            sensorType: sensor.sensorType,
            moduleId: this.modulo,
            moduleName: currentModule?.name || '',
            fincaId: this.finca,
            fincaName: currentFinca?.name || '',
            clientId: currentClientId,
            value: storedValues.get(sensor.idSensor) ?? 0
          } as SensorGraphSelection;
        });
        this.updateHumidityState();
        this.spinner.hide();
      },
      () => {
        this.spinner.hide();
      }
    );
  }

  loadStatistics(): void {
    const requestId = ++this.statsRequestId;
    this.axisState.chart1.clear();
    this.axisState.chart2.clear();

    const zonesAt = this.getZonesAtDate();
    const startDate = this.dateRange?.start
      ? this.stringStartDate(this.dateRange.start)
      : '';
    const endDate = this.dateRange?.end
      ? this.stringDate(this.dateRange.end)
      : '';

    const selectedSensors = this.getSelectedSensorsForStatistics();
    const compareMultipleModules =
      new Set(selectedSensors.map(selection => selection.moduleId)).size > 1;

    this.updateHumidityState(selectedSensors);

    if (selectedSensors.length === 0) {
      this.hasPrimaryChart = false;
      this.hasSecondChart = false;
      this.destroyCharts();
      return;
    }

    const humindString =
      this.sensorHumedadActivado && this.humind.length
        ? this.humind.join('-')
        : '';
    const humsumString =
      this.sensorHumedadActivado && this.humsum.length
        ? this.humsum.join('-')
        : '';

    const requests: Array<Observable<
      SensorRequestResult
    >> = selectedSensors.map(selection =>
      this.buildSensorRequest(
        selection,
        startDate,
        endDate,
        zonesAt,
        humindString,
        humsumString
      )
    );

    if (!requests.length) {
      this.spinner.hide();
      return;
    }

    this.spinner.show();
    forkJoin(requests).subscribe(
      (results: SensorRequestResult[]) => {
        if (requestId !== this.statsRequestId) {
          this.spinner.hide();
          return;
        }

        const chartSeries: Record<ChartKey, Highcharts.SeriesOptionsType[]> = {
          chart1: [],
          chart2: []
        };

        let eventsSeries: Highcharts.SeriesOptionsType | null = null;

        for (const result of results) {
          if (!result.data) {
            continue;
          }

          if (!compareMultipleModules) {
            eventsSeries =
              eventsSeries || this.buildEventsSeries(result.data?.events?.data);
          }

          const target: ChartKey =
            result.selection.value === 2 ? 'chart2' : 'chart1';
          const sensorsIds = Object.keys(result.data).filter(
            key => key !== 'events'
          );
          const filteredSensorIds = this.shouldRestrictHumidityResponseToSelection(
            result.selection,
            sensorsIds
          )
            ? sensorsIds.filter(
                key => Number(key) === Number(result.selection.idSensor)
              )
            : sensorsIds;

          for (const sensorKey of filteredSensorIds) {
            const sensorData = result.data[sensorKey];
            const seriesForSensor = this.buildSeriesForSensor(
              result.selection,
              Number(sensorKey),
              sensorData,
              result.zonesMap,
              compareMultipleModules
            );
            if (seriesForSensor.length) {
              chartSeries[target].push(...seriesForSensor);
            }
          }
        }

        if (eventsSeries) {
          if (chartSeries.chart1.length) {
            chartSeries.chart1.push(eventsSeries);
          }
          if (chartSeries.chart2.length) {
            chartSeries.chart2.push(eventsSeries);
          }
        }

        this.hasPrimaryChart = chartSeries.chart1.length > 0;
        this.hasSecondChart = chartSeries.chart2.length > 0;

        if (!this.hasPrimaryChart) {
          this.destroyCharts();
          this.spinner.hide();
          return;
        }

        const schedule =
          typeof requestAnimationFrame !== 'undefined'
            ? requestAnimationFrame
            : (cb: () => void) => setTimeout(cb, 0);

        schedule(() => {
          if (requestId !== this.statsRequestId) {
            this.spinner.hide();
            return;
          }
          this.renderChart('chart1', chartSeries.chart1);
          if (this.hasSecondChart) {
            this.renderChart('chart2', chartSeries.chart2);
          } else {
            this.destroyChart('chart2');
          }
          this.updateRangeControls(this.chart1, true, !this.hasSecondChart);
          this.updateRangeControls(
            this.chart2,
            !this.hasSecondChart ? true : false,
            this.hasSecondChart
          );
          this.scheduleResize();
          this.scheduleChartAlignment();
          this.spinner.hide();
        });
      },
      () => {
        this.snackBar.open(
          this.translate.instant('STATICS.ERROR_DATA'),
          this.translate.instant('COMMON.CLOSE'),
          {
            duration: 5000
          }
        );
        this.spinner.hide();
      }
    );
  }

  private shouldRestrictHumidityResponseToSelection(
    selection: SensorGraphSelection,
    sensorIds: string[]
  ): boolean {
    return (
      (selection.sensorType === 'SC' || selection.sensorType === 'SH') &&
      sensorIds.some(key => Number(key) === Number(selection.idSensor))
    );
  }

  private buildSensorRequest(
    selection: SensorGraphSelection,
    startDate: string,
    endDate: string,
    zonesAt: string,
    humind: string,
    humsum: string
  ): Observable<SensorRequestResult> {
    let req$: Observable<any> | null = null;

    if (this.isHumiditySensorType(selection.sensorType)) {
      req$ = this.sensorDataService.getHumidityData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId,
        sensorDataColumn: humind,
        sensorDataColumnSumatory: humsum,
        sensorType: selection.sensorType
      });
    } else if (selection.sensorType === 'FL') {
      req$ = this.sensorDataService.getFlowmeterData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'WC') {
      req$ = this.sensorDataService.getConductivityData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'TE') {
      req$ = this.sensorDataService.getThermometerData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'HH') {
      req$ = this.sensorDataService.getLeafHumidityData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'DE') {
      req$ = this.sensorDataService.getDendrometerData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'ED' || selection.sensorType === 'DIGITAL') {
      req$ = this.sensorDataService.getDigitalData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'CO') {
      req$ = this.sensorDataService.getConductivityData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'AN') {
      req$ = this.sensorDataService.getAnemometerData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    } else if (selection.sensorType === 'PL') {
      req$ = this.sensorDataService.getPluviometerData({
        minDate: startDate,
        maxDate: endDate,
        idModule: selection.moduleId
      });
    }

    if (!req$) {
      return of({
        selection,
        data: null,
        zonesMap: new Map() as Map<number, MoistureZonePeriod[]>
      } as SensorRequestResult);
    }

    return (req$ as Observable<any>).pipe(
      switchMap((data: any) => {
        if (!this.isHumiditySensorType(selection.sensorType) || !data) {
          return of({
            data,
            zonesMap: new Map() as Map<number, MoistureZonePeriod[]>
          });
        }
        const sensorIds = Object.keys(data)
          .map(value => Number(value))
          .filter(value => Number.isFinite(value));
        const zonesRequests = sensorIds.map(sensorId =>
          this.getMoistureZonePeriods(sensorId, zonesAt, selection.clientId).pipe(
            map(periods => ({ sensorId, periods }))
          )
        );
        if (!zonesRequests.length) {
          return of({
            data,
            zonesMap: new Map() as Map<number, MoistureZonePeriod[]>
          });
        }
        return forkJoin(zonesRequests).pipe(
          map(items => {
            const zonesMap = new Map<number, MoistureZonePeriod[]>();
            for (const item of items) {
              zonesMap.set(item.sensorId, item.periods);
            }
            return { data, zonesMap };
          })
        );
      }),
      map(payload => ({
        selection,
        data: payload.data,
        zonesMap: payload.zonesMap
      }))
    );
  }

  private buildSeriesForSensor(
    selection: SensorGraphSelection,
    sensorId: number,
    sensorData: any,
    zonesMap: Map<number, MoistureZonePeriod[]>,
    includeModuleName: boolean
  ): Highcharts.SeriesOptionsType[] {
    sensorData = this.transformEnviroproPayload(
      selection.sensorType,
      sensorData
    );
    const seriesList: Highcharts.SeriesOptionsType[] = [];

    if (!sensorData || typeof sensorData !== 'object') {
      return seriesList;
    }

    if (selection.sensorType === 'AN') {
      const windbarbSeries = this.buildWindbarbSeries(
        selection,
        sensorData,
        includeModuleName
      );
      if (windbarbSeries.length) {
        seriesList.push(...windbarbSeries);
      }
    }

    const graphNames = Object.keys(sensorData);

    for (const graphName of graphNames) {
      if (
        selection.sensorType === 'AN' &&
        graphName === 'AN_dv' &&
        seriesList.some(s => (s as any).type === 'windbarb')
      ) {
        continue;
      }
      const graph = sensorData[graphName];
      if (!graph || !Array.isArray(graph.data) || graph.data.length === 0) {
        continue;
      }

      const normalizedGraphName = this.normalizeHumidityGraphName(
        selection.sensorType,
        graphName
      );
      if (
        selection.sensorType === 'SC' &&
        ['hum', 'SC_hum'].includes(normalizedGraphName) &&
        this.hasGraphData(sensorData.sumatoryControl)
      ) {
        continue;
      }
      const isConductivityDepth =
        selection.sensorType === 'E8' && /^C\d{2}$/.test(normalizedGraphName);
      if (isConductivityDepth) {
        if (!this.conductivitySelected.length) {
          continue;
        }
        if (!this.conductivitySelected.includes(normalizedGraphName)) {
          continue;
        }
      }

      if (
        selection.sensorType === 'E8' &&
        /^T\d{1,2}$/.test(normalizedGraphName)
      ) {
        continue;
      }
      const meta = this.getSeriesMeta(
        selection.sensorType,
        normalizedGraphName
      );
      if (!meta) {
        continue;
      }

      const depthMatch = this.getHumiditySelectionKey(
        selection.sensorType,
        normalizedGraphName
      );
      if (
        depthMatch &&
        selection.sensorType !== 'SH' &&
        selection.sensorType !== 'SC'
      ) {
        if (!this.humind.length) {
          continue;
        }
        if (!this.humind.includes(depthMatch)) {
          continue;
        }
      }

      const points = graph.data
        .map((value: any) => {
          if (!Array.isArray(value) || value.length < 2) {
            return null;
          }
          const x = this.toEpochMs(value[0]);
          const y = parseFloat(value[1]);
          return x !== null && Number.isFinite(y) ? [x, y] : null;
        })
        .filter(Boolean) as Array<[number, number]>;

      if (!points.length) {
        continue;
      }

      const series: Highcharts.SeriesOptionsType = {
        id: `${selection.moduleId}-${selection.idSensor}-${graphName}`,
        name: this.seriesLabel(meta.nameKey, selection.moduleName, includeModuleName),
        type: (meta.type as any) || 'spline',
        data: points,
        marker: {
          enabled: false
        }
      };

      if (meta.suffix || meta.decimals !== undefined) {
        series.tooltip = {
          ...(meta.suffix ? { valueSuffix: meta.suffix } : {}),
          ...(meta.decimals !== undefined
            ? { valueDecimals: meta.decimals }
            : {})
        } as any;
      }

      (series as any).i18nKey = meta.nameKey;
      (series as any).moduleName = selection.moduleName;
      (series as any).includeModuleName = includeModuleName;

      const values = points.map(p => p[1]);
      const axisKey = meta.group || graphName;
      const unit = meta.unit || (meta.suffix ? meta.suffix : null);
      const axisIndex = this.resolveAxisIndex(
        selection.value === 2 ? 'chart2' : 'chart1',
        axisKey,
        unit,
        values
      );
      (series as any).yAxis = axisIndex;

      if (meta.applyZones) {
        const zonePeriods = zonesMap.get(sensorId) || [];
        const segments = this.splitPointsByMoistureZones(points, zonePeriods);
        const baseSeriesId = String((series as any).id);
        segments.forEach((segment, index) => {
          const segmentSeries = {
            ...(series as any),
            id: index === 0 ? baseSeriesId : `${baseSeriesId}-zone-${index}`,
            data: segment.points,
            zoneAxis: 'y',
            zones: segment.zones,
            showInLegend: index === 0,
            linkedTo: index === 0 ? undefined : baseSeriesId
          } as Highcharts.SeriesOptionsType;
          seriesList.push(segmentSeries);
        });
        continue;
      }

      seriesList.push(series);
    }

    return seriesList;
  }

  private hasGraphData(graph: any): boolean {
    return !!graph && Array.isArray(graph.data) && graph.data.length > 0;
  }

  private getMoistureZonePeriods(
    sensorId: number,
    fallbackAt: string,
    clientId?: number | null
  ): Observable<MoistureZonePeriod[]> {
    const fallback$ = this.sensorMoistureZonesService
      .getZones(sensorId, fallbackAt)
      .pipe(
        map(zones =>
          this.fullRangeMoisturePeriod(
            zones?.zones || DEFAULT_HU_CONTROL_ZONES
          )
        ),
        catchError(() =>
          of(this.fullRangeMoisturePeriod(DEFAULT_HU_CONTROL_ZONES))
        )
      );

    return this.sensorMoistureProfileService.getSensorProfile(sensorId).pipe(
      switchMap((profile: any) => {
        const profileId = this.extractMoistureProfileId(profile);
        if (!Number.isFinite(profileId) || profileId <= 0) {
          return fallback$;
        }
        return this.moistureProfileAdminService
          .listSeasons(
            profileId,
            Number.isFinite(Number(clientId)) && Number(clientId) > 0
              ? Number(clientId)
              : undefined
          )
          .pipe(
            map((response: any) =>
              this.buildMoisturePeriodsFromSeasons(
                this.extractMoistureSeasons(response)
              )
            ),
            switchMap(periods => (periods.length ? of(periods) : fallback$)),
            catchError(() => fallback$)
          );
      }),
      catchError(() => fallback$)
    );
  }

  private extractMoistureProfileId(profile: any): number {
    return Number(
      profile?.profileId ??
        profile?.idMoistureProfile ??
        profile?.id ??
        profile?.data?.profileId ??
        profile?.data?.idMoistureProfile ??
        profile?.data?.id
    );
  }

  private extractMoistureSeasons(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response?.items)) {
      return response.items;
    }
    return [];
  }

  private fullRangeMoisturePeriod(
    zones: Array<{ value?: number; color?: string }>
  ): MoistureZonePeriod[] {
    return [
      {
        startMs: Number.NEGATIVE_INFINITY,
        endMs: Number.POSITIVE_INFINITY,
        priority: 0,
        id: 0,
        zones
      }
    ];
  }

  private buildMoisturePeriodsFromSeasons(seasons: any): MoistureZonePeriod[] {
    if (
      !Array.isArray(seasons) ||
      !this.dateRange?.start ||
      !this.dateRange?.end
    ) {
      return [];
    }

    const startYear = this.dateRange.start.getFullYear() - 1;
    const endYear = this.dateRange.end.getFullYear() + 1;
    const periods: MoistureZonePeriod[] = [];

    for (const season of seasons) {
      if (!this.isMoistureSeasonEnabled(season)) {
        continue;
      }
      const startMmdd = String(season?.startMmdd || '');
      const endMmdd = String(season?.endMmdd || '');
      if (!/^\d{2}-\d{2}$/.test(startMmdd) || !/^\d{2}-\d{2}$/.test(endMmdd)) {
        continue;
      }
      const threshold1 = Number(season?.threshold1);
      const threshold2 = Number(season?.threshold2);
      if (!Number.isFinite(threshold1) || !Number.isFinite(threshold2)) {
        continue;
      }

      for (let year = startYear; year <= endYear; year += 1) {
        const start = this.mmddToDateInYear(startMmdd, year, false);
        const endYearForSeason = startMmdd <= endMmdd ? year : year + 1;
        const end = this.mmddToDateInYear(endMmdd, endYearForSeason, true);
        periods.push({
          startMs: start.getTime(),
          endMs: end.getTime(),
          priority: Number(season?.priority) || 0,
          id: Number(season?.idMoistureProfileSeason ?? season?.id) || 0,
          zones: this.buildMoistureZones(threshold1, threshold2)
        });
      }
    }

    return periods.sort((a, b) => a.startMs - b.startMs);
  }

  private isMoistureSeasonEnabled(season: any): boolean {
    if (
      !season ||
      typeof season !== 'object' ||
      (!('isEnabled' in season) && !('isActive' in season))
    ) {
      return true;
    }
    const value = 'isEnabled' in season ? season.isEnabled : season.isActive;
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private mmddToDateInYear(mmdd: string, year: number, endOfDay: boolean): Date {
    const [month, day] = mmdd.split('-').map(value => Number(value));
    return endOfDay
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private buildMoistureZones(
    threshold1: number,
    threshold2: number
  ): Array<{ value?: number; color?: string }> {
    return [
      { value: 0, color: '#d32f2f' },
      { value: threshold1, color: '#f57c00' },
      { value: threshold2, color: '#fbc02d' },
      { value: 100, color: '#2e7d32' },
      { color: '#1565c0' }
    ];
  }

  private splitPointsByMoistureZones(
    points: Array<[number, number]>,
    periods: MoistureZonePeriod[]
  ): Array<{
    points: Array<[number, number]>;
    zones: Array<{ value?: number; color?: string }>;
  }> {
    const segments: Array<{
      points: Array<[number, number]>;
      zones: Array<{ value?: number; color?: string }>;
      key: string;
    }> = [];

    for (const point of points) {
      const zones = this.findZonesForPoint(point[0], periods);
      const key = JSON.stringify(zones);
      const last = segments[segments.length - 1];
      if (!last || last.key !== key) {
        segments.push({ points: [point], zones, key });
      } else {
        last.points.push(point);
      }
    }

    return segments.map(({ points: segmentPoints, zones }) => ({
      points: segmentPoints,
      zones
    }));
  }

  private findZonesForPoint(
    timestamp: number,
    periods: MoistureZonePeriod[]
  ): Array<{ value?: number; color?: string }> {
    const matches = periods
      .filter(period => timestamp >= period.startMs && timestamp <= period.endMs)
      .sort((a, b) => b.priority - a.priority || b.id - a.id);
    return matches[0]?.zones || DEFAULT_HU_CONTROL_ZONES;
  }

  private toEpochMs(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number') {
      const date = new Date(value);
      const time = date.getTime();
      return Number.isNaN(time) ? null : Math.floor(time / 1000) * 1000;
    }

    if (typeof value === 'string') {
      const match = value.match(
        /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?)?(Z|[+-]\d{2}:?\d{2})?$/
      );
      if (match) {
        const [, y, m, d, hh = '00', mm = '00', ss = '00', tzRaw] = match;
        if (tzRaw) {
          let tz = tzRaw;
          if (tz !== 'Z' && tz.length === 5) {
            tz = `${tz.slice(0, 3)}:${tz.slice(3)}`;
          }
          const iso = `${y}-${m}-${d}T${hh}:${mm}:${ss}${tz}`;
          const parsed = new Date(iso);
          const time = parsed.getTime();
          return Number.isNaN(time) ? null : Math.floor(time / 1000) * 1000;
        }

        const local = new Date(
          Number(y),
          Number(m) - 1,
          Number(d),
          Number(hh),
          Number(mm),
          Number(ss)
        );
        const time = local.getTime();
        return Number.isNaN(time) ? null : Math.floor(time / 1000) * 1000;
      }
    }

    const date = new Date(value as any);
    const time = date.getTime();
    return Number.isNaN(time) ? null : Math.floor(time / 1000) * 1000;
  }

  private buildWindbarbSeries(
    selection: SensorGraphSelection,
    sensorData: any,
    includeModuleName: boolean
  ): Highcharts.SeriesOptionsType[] {
    const speedGraph = sensorData?.['AN_vv'];
    const dirGraph = sensorData?.['AN_dv'];
    if (
      !speedGraph?.data ||
      !Array.isArray(speedGraph.data) ||
      !dirGraph?.data ||
      !Array.isArray(dirGraph.data)
    ) {
      return [];
    }

    const directionByX = new Map<number, number>();
    for (const value of dirGraph.data) {
      if (!Array.isArray(value) || value.length < 2) {
        continue;
      }
      const x = this.toEpochMs(value[0]);
      const dir = parseFloat(value[1]);
      if (x === null || !Number.isFinite(dir)) {
        continue;
      }
      directionByX.set(x, dir);
    }

    const points: Array<[number, number, number]> = [];
    for (const value of speedGraph.data) {
      if (!Array.isArray(value) || value.length < 2) {
        continue;
      }
      const x = this.toEpochMs(value[0]);
      if (x === null) {
        continue;
      }
      const speed = parseFloat(value[1]);
      const direction = directionByX.get(x);
      if (!Number.isFinite(speed) || !Number.isFinite(direction as number)) {
        continue;
      }
      points.push([x, speed, direction as number]);
    }

    if (!points.length) {
      return [];
    }

    const windbarbSeries: Highcharts.SeriesOptionsType = {
      id: `${selection.moduleId}-${selection.idSensor}-windbarb`,
      name: this.seriesLabel(
        'STATICS.SERIES.WIND_DIRECTION',
        selection.moduleName,
        includeModuleName
      ),
      type: 'windbarb' as any,
      data: points,
      tooltip: {
        pointFormatter: function() {
          const direction = (this.point as any).direction;
          const labels: string[] =
            (this.series as any).directionLabels ||
            (this.series as any).userOptions.directionLabels ||
            [];
          if (typeof direction === 'number') {
            const idx = Math.round(((direction % 360) + 360) / 22.5) % 16;
            const label = labels[idx] || '';
            return label || `${direction}°`;
          }
          return '';
        } as any
      },
      dataGrouping: {
        enabled: true,
        forced: true,
        approximation: 'windbarb',
        groupPixelWidth: 30,
        units: [['minute', [30, 60, 120, 180]]]
      },
      vectorLength: 12
    };

    (windbarbSeries as any).directionLabels = this.getWindDirectionLabels();
    (windbarbSeries as any).i18nKey = 'STATICS.SERIES.WIND_DIRECTION';
    (windbarbSeries as any).moduleName = selection.moduleName;
    (windbarbSeries as any).includeModuleName = includeModuleName;

    return [windbarbSeries];
  }

  private buildEventsSeries(
    eventsData: any
  ): Highcharts.SeriesOptionsType | null {
    if (!Array.isArray(eventsData) || eventsData.length === 0) {
      return null;
    }

    const data = eventsData
      .map((element: any) => {
        if (!Array.isArray(element) || element.length < 2) {
          return null;
        }
        const x = this.toEpochMs(element[0]);
        if (x === null) {
          return null;
        }
        return {
          x,
          title: element[1],
          text: element[1]
        };
      })
      .filter(Boolean);

    if (!data.length) {
      return null;
    }

    return {
      id: 'events',
      name: this.seriesLabel('STATICS.SERIES.EVENTS'),
      type: 'flags',
      data,
      tooltip: {
        pointFormat: ' '
      },
      yAxis: 0
    } as any;
  }

  private resolveAxisIndex(
    chartKey: ChartKey,
    axisKey: string,
    unit: string | null,
    values: number[]
  ): number {
    const map = this.axisState[chartKey];
    const seriesMin = values.length ? Math.min(...values) : 0;
    const seriesMax = values.length ? Math.max(...values) : seriesMin;
    const compatibilityFactor = 1.2;

    const existing = map.get(axisKey);
    if (existing) {
      existing.min = Math.min(existing.min, seriesMin);
      existing.max = Math.max(existing.max, seriesMax);
      return existing.id;
    }

    for (const state of map.values()) {
      if (
        state.unit === unit &&
        this.areRangesCompatible(
          state,
          { min: seriesMin, max: seriesMax },
          compatibilityFactor
        )
      ) {
        state.min = Math.min(state.min, seriesMin);
        state.max = Math.max(state.max, seriesMax);
        map.set(axisKey, state);
        return state.id;
      }
    }

    const id = map.size;
    map.set(axisKey, { id, unit, min: seriesMin, max: seriesMax });
    return id;
  }

  private areRangesCompatible(
    existing: { min: number; max: number },
    next: { min: number; max: number },
    factor: number
  ): boolean {
    const existingRange = Math.max(1, Math.abs(existing.max - existing.min));
    const nextRange = Math.max(1, Math.abs(next.max - next.min));
    const combinedRange =
      Math.max(existing.max, next.max) - Math.min(existing.min, next.min);
    const spread = combinedRange / Math.max(existingRange, nextRange);
    return spread <= factor;
  }

  private renderChart(
    chartKey: ChartKey,
    series: Highcharts.SeriesOptionsType[]
  ): void {
    const container = this.getChartContainer(chartKey);
    if (!container || !series.length) {
      this.destroyChart(chartKey);
      return;
    }

    const options = Highcharts.merge({}, baseOptions);
    this.applyRangeSelectorLabels(options);
    this.applyThemeToOptions(options, this.getHighchartsTheme());

    const hasWindbarb = series.some(s => (s as any).type === 'windbarb');
    const axisIndices = series
      .map(s => (s as any).yAxis)
      .filter(value => typeof value === 'number') as number[];
    const maxAxisIndex = axisIndices.length ? Math.max(...axisIndices) : 0;
    const yAxisCount = Math.max(maxAxisIndex + 1, 1);
    options.yAxis = this.buildYAxis(yAxisCount, this.getHighchartsTheme());

    const xAxisConfig = Array.isArray(options.xAxis)
      ? options.xAxis[0]
      : options.xAxis || {};
    options.xAxis = {
      ...(xAxisConfig as any),
      type: 'datetime',
      crosshair: true,
      offset: hasWindbarb ? 40 : (xAxisConfig as any).offset,
      events: {
        ...(xAxisConfig as any).events,
        setExtremes: this.syncExtremes.bind(this)
      }
    } as any;

    options.series = series;
    options.lang = this.getHighchartsLang() as any;
    options.chart = {
      ...(options.chart || {}),
      height: this.getChartHeight(container),
      width: container.clientWidth || undefined
    } as any;

    this.destroyChart(chartKey);
    const chart = Highcharts.stockChart(container, options);
    if (chartKey === 'chart1') {
      this.chart1 = chart;
    } else {
      this.chart2 = chart;
    }
    (chart as any).chartKey = chartKey;

    this.updateAxisColors(chart);
    this.attachResizeObserver(chartKey, container);
    this.rebuildSyncIndex(chartKey);
    this.applyLegendAlignment(chart);
    this.attachSyncHandlers(chartKey, chart);
  }

  private buildYAxis(count: number, theme: any): Highcharts.YAxisOptions[] {
    const axes: Highcharts.YAxisOptions[] = [];
    for (let i = 0; i < count; i++) {
      axes.push({
        labels: {
          enabled: true,
          style: theme?.yAxis?.labels?.style
        },
        title: {
          text: null,
          style: theme?.yAxis?.title?.style
        },
        gridLineColor: theme?.yAxis?.gridLineColor,
        lineColor: theme?.yAxis?.lineColor,
        tickColor: theme?.yAxis?.tickColor,
        opposite: i % 2 === 1
      });
    }
    return axes.length ? axes : [{ title: { text: '' } }];
  }

  private destroyChart(chartKey: ChartKey): void {
    const chart = chartKey === 'chart1' ? this.chart1 : this.chart2;
    if (chart) {
      chart.destroy();
    }
    if (chartKey === 'chart1') {
      this.chart1 = undefined;
    } else {
      this.chart2 = undefined;
    }
  }

  private destroyCharts(): void {
    this.destroyChart('chart1');
    this.destroyChart('chart2');
  }

  private getChartContainer(chartKey: ChartKey): HTMLElement | null {
    if (chartKey === 'chart1') {
      return this.chart1Container ? this.chart1Container.nativeElement : null;
    }
    return this.chart2Container ? this.chart2Container.nativeElement : null;
  }

  private getChartHeight(container: HTMLElement): number {
    const containerHeight = container.clientHeight;
    if (containerHeight > 0) {
      return containerHeight;
    }
    const wrapper = this.mainGraficas?.nativeElement;
    const available = wrapper ? wrapper.clientHeight : 0;
    const base = available || 420;
    return this.hasSecondChart ? Math.max(320, base / 2) : Math.max(360, base);
  }

  private attachResizeObserver(
    chartKey: ChartKey,
    container: HTMLElement
  ): void {
    const existing = this.resizeObservers.get(chartKey);
    if (existing) {
      existing.disconnect();
    }
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => this.scheduleResize());
    observer.observe(container);
    this.resizeObservers.set(chartKey, observer);
  }

  private disconnectResizeObservers(): void {
    for (const observer of this.resizeObservers.values()) {
      observer.disconnect();
    }
    this.resizeObservers.clear();
  }

  private scheduleResize(): void {
    if (this.resizeRaf !== null) {
      return;
    }
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: () => void) => setTimeout(cb, 0);
    this.resizeRaf = schedule(() => {
      this.resizeRaf = null;
      this.resizeCharts();
    }) as any;
  }

  private resizeCharts(): void {
    this.resizeChartInstance(this.chart1, this.chart1Container?.nativeElement);
    this.resizeChartInstance(this.chart2, this.chart2Container?.nativeElement);
    this.scheduleChartAlignment();
    this.scheduleSyncFromEvent(null, 'refresh');
  }

  private resizeChartInstance(
    chart: Highcharts.Chart | undefined,
    container?: HTMLElement
  ): void {
    if (!chart || !container) {
      return;
    }
    const width = container.clientWidth;
    const height = this.getChartHeight(container);
    if (width > 0 && height > 0) {
      chart.setSize(width, height, false);
      chart.redraw(false);
    }
  }

  private scheduleChartAlignment(): void {
    if (!this.hasSecondChart || !this.chart1 || !this.chart2) {
      return;
    }
    if (this.alignRaf !== null) {
      return;
    }
    const scheduler =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: () => void) => setTimeout(cb, 0);
    this.alignRaf = scheduler(() => {
      this.alignRaf = null;
      this.alignTwoChartsPlotArea();
    }) as any;
  }

  private alignTwoChartsPlotArea(): void {
    if (this.isAligning) {
      return;
    }
    const chartA = this.chart1;
    const chartB = this.chart2;
    if (!chartA || !chartB) {
      return;
    }
    this.isAligning = true;
    try {
      const leftA = chartA.plotLeft;
      const rightA = chartA.chartWidth - (chartA.plotLeft + chartA.plotWidth);
      const leftB = chartB.plotLeft;
      const rightB = chartB.chartWidth - (chartB.plotLeft + chartB.plotWidth);

      const targetLeft = Math.max(leftA, leftB);
      const targetRight = Math.max(rightA, rightB);
      const threshold = 1;

      let updated = false;
      if (
        Math.abs(leftA - targetLeft) > threshold ||
        Math.abs(rightA - targetRight) > threshold
      ) {
        chartA.update(
          {
            chart: {
              marginLeft: targetLeft,
              marginRight: targetRight
            }
          },
          false
        );
        updated = true;
      }

      if (
        Math.abs(leftB - targetLeft) > threshold ||
        Math.abs(rightB - targetRight) > threshold
      ) {
        chartB.update(
          {
            chart: {
              marginLeft: targetLeft,
              marginRight: targetRight
            }
          },
          false
        );
        updated = true;
      }

      if (updated) {
        chartA.redraw(false);
        chartB.redraw(false);
      }
    } finally {
      this.isAligning = false;
    }
  }

  private attachSyncHandlers(
    chartKey: ChartKey,
    chart: Highcharts.Chart
  ): void {
    const container = chart.container;
    const moveHandler = (event: MouseEvent | TouchEvent) =>
      this.scheduleSyncFromEvent(chartKey, 'move', event);
    const leaveHandler = () => this.scheduleSyncFromEvent(chartKey, 'leave');
    container.addEventListener('mousemove', moveHandler);
    container.addEventListener('touchmove', moveHandler, { passive: true });
    container.addEventListener('touchstart', moveHandler, { passive: true });
    container.addEventListener('mouseleave', leaveHandler);
    container.addEventListener('touchend', leaveHandler);
  }

  private scheduleSyncFromEvent(
    source: ChartKey | null,
    type: 'move' | 'leave' | 'refresh',
    event?: MouseEvent | TouchEvent
  ): void {
    if (!this.hasSecondChart || !this.chart1 || !this.chart2) {
      return;
    }
    if (this.syncRaf !== null) {
      return;
    }
    const scheduler =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: () => void) => setTimeout(cb, 0);
    this.syncRaf = scheduler(() => {
      this.syncRaf = null;
      if (type === 'refresh') {
        this.rebuildSyncIndex('chart1');
        this.rebuildSyncIndex('chart2');
        return;
      }
      if (!source) {
        return;
      }
      const target = source === 'chart1' ? 'chart2' : 'chart1';
      this.syncChartsOnEvent(source, target, event, type);
    }) as any;
  }

  private syncChartsOnEvent(
    sourceKey: ChartKey,
    targetKey: ChartKey,
    event: MouseEvent | TouchEvent | undefined,
    type: 'move' | 'leave'
  ): void {
    if (this.isSyncing) {
      return;
    }
    const sourceChart = sourceKey === 'chart1' ? this.chart1 : this.chart2;
    const targetChart = targetKey === 'chart1' ? this.chart1 : this.chart2;
    if (!sourceChart || !targetChart) {
      return;
    }

    if (type === 'leave') {
      targetChart.tooltip?.hide();
      if (targetChart.xAxis && targetChart.xAxis[0]) {
        (targetChart.xAxis[0] as any).hideCrosshair?.();
      }
      return;
    }

    const pointer = sourceChart.pointer;
    if (!pointer || !event) {
      return;
    }
    const normalized = pointer.normalize(event as any);
    const hoverPoint = this.getHoverPoint(sourceChart, normalized);
    if (!hoverPoint || typeof hoverPoint.x !== 'number') {
      this.syncChartsOnEvent(sourceKey, targetKey, undefined, 'leave');
      return;
    }

    const matches = this.getExactPoints(targetKey, hoverPoint.x);
    if (!matches.length) {
      this.syncChartsOnEvent(sourceKey, targetKey, undefined, 'leave');
      return;
    }

    this.isSyncing = true;
    try {
      targetChart.tooltip.refresh(matches);
      if (targetChart.xAxis && targetChart.xAxis[0]) {
        targetChart.xAxis[0].drawCrosshair(normalized as any, matches[0]);
      }
    } finally {
      this.isSyncing = false;
    }
  }

  private getHoverPoint(
    chart: Highcharts.Chart,
    normalizedEvent: any
  ): Highcharts.Point | null {
    const hoverPoint = (chart as any).hoverPoint as
      | Highcharts.Point
      | undefined;
    if (hoverPoint) {
      return hoverPoint;
    }
    const hoverPoints = (chart as any).hoverPoints as
      | Highcharts.Point[]
      | undefined;
    if (hoverPoints && hoverPoints.length) {
      return hoverPoints[0];
    }
    for (const series of chart.series) {
      if (!series.visible || !series.searchPoint) {
        continue;
      }
      const p = series.searchPoint(normalizedEvent, true);
      if (p) {
        return p;
      }
    }
    return null;
  }

  private rebuildSyncIndex(chartKey: ChartKey): void {
    const chart = chartKey === 'chart1' ? this.chart1 : this.chart2;
    const map = this.syncIndex[chartKey];
    map.clear();
    if (!chart) {
      return;
    }
    for (const series of chart.series) {
      if (!series.visible || !series.points || !series.points.length) {
        continue;
      }
      for (const point of series.points) {
        const x = point.x as number;
        if (typeof x !== 'number') {
          continue;
        }
        if (!map.has(x)) {
          map.set(x, []);
        }
        map.get(x)?.push(point);
      }
    }
  }

  private getExactPoints(chartKey: ChartKey, x: number): Highcharts.Point[] {
    const map = this.syncIndex[chartKey];
    return map.get(x) || [];
  }

  private getChartKey(chart: Highcharts.Chart | undefined): ChartKey | null {
    if (!chart) {
      return null;
    }
    if (chart === this.chart1) {
      return 'chart1';
    }
    if (chart === this.chart2) {
      return 'chart2';
    }
    return (chart as any).chartKey ?? null;
  }
  private applyLegendAlignment(chart: Highcharts.Chart | undefined): void {
    if (!chart) {
      return;
    }
    const scheduler =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: () => void) => setTimeout(cb, 0);
    let needsRedraw = false;
    const component = this;
    for (const series of chart.series) {
      if ((series as any)._legendAlignWrapped) {
        continue;
      }
      const currentEvents = ((series.options as any).events ||
        {}) as Highcharts.SeriesEventsOptionsObject;
      const userLegendHandler = currentEvents.legendItemClick;
      const wrappedLegendHandler = function(this: Highcharts.Series) {
        if (typeof userLegendHandler === 'function') {
          userLegendHandler.call(this);
        }
        scheduler(() => {
          component.scheduleChartAlignment();
          const key = component.getChartKey(this.chart);
          if (key) {
            component.rebuildSyncIndex(key);
            component.scheduleSyncFromEvent(null, 'refresh');
          }
        });
        return undefined;
      };

      const newEvents = {
        ...currentEvents,
        legendItemClick: wrappedLegendHandler
      };
      if (currentEvents.legendItemClick !== wrappedLegendHandler) {
        series.update({ events: newEvents } as any, false);
        needsRedraw = true;
      }
      (series as any)._legendAlignWrapped = true;
    }
    if (needsRedraw) {
      chart.redraw(false);
    }
  }

  private registerLanguageListener(): void {
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.applyGlobalHighchartsOptions();
        this.localizeSensorTypes();
        this.refreshSensorNames();
        this.refreshSeriesTranslations();
        const lang = this.getHighchartsLang();
        const charts = [this.chart1, this.chart2];
        for (const chart of charts) {
          if (!chart) {
            continue;
          }
          const opts = Highcharts.merge({}, chart.options);
          this.applyRangeSelectorLabels(opts);
          chart.update(
            {
              lang,
              rangeSelector: opts.rangeSelector
            },
            false
          );
          this.applyThemeToChart(chart);
          chart.redraw();
        }
        this.scheduleChartAlignment();
      })
    );
  }

  private registerThemeListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('app-theme-change', this.themeChangeHandler);
    }
  }

  private onThemeChanged(): void {
    this.applyGlobalHighchartsOptions();
    this.applyThemeToChart(this.chart1);
    this.applyThemeToChart(this.chart2);
    this.scheduleResize();
    this.scheduleChartAlignment();
  }

  private loadSensorTypes(): void {
    const sub = this.sensorService.sensorTypes.pipe(debounceTime(60)).subscribe(
      sensorTypes => {
        this.sensorTypes = sensorTypes;
        this.localizeSensorTypes();
        this.refreshSensorNames();
        if (this.fincas.length === 0) {
          this.loadFincas();
        } else {
          this.spinner.hide();
        }
      },
      () => {
        this.spinner.hide();
      }
    );
    this.subscriptions.add(sub);

    this.sensorService.getSensorTypes().subscribe(
      types => this.sensorService.sensorTypes.next(types),
      () => {}
    );
  }

  private loadFincas(): void {
    this.$http.default.get('/admin/finca').subscribe(
      data => {
        this.fincas = (data || []).filter(
          (finca: any) => !this.isDeletedEntity(finca)
        );
        this.finca = this.fincas.length ? this.fincas[0].idFinca : -1;
        if (this.finca !== -1) {
          this.fincaChange();
        }
        this.spinner.hide();
      },
      () => {
        this.spinner.hide();
      }
    );
  }

  private setOrder(sensores: any[]): any[] {
    let result = [];
    let currentIndex = 3;

    for (const sensor of sensores) {
      switch (sensor.sensorType) {
        case 'TE':
        case 'HH':
          sensor.order = 0;
          break;
        case 'HU':
        case 'E8':
        case 'SH':
        case 'SC':
          sensor.order = 1;
          break;
        case 'FL':
          sensor.order = 2;
          break;
        default:
          sensor.order = currentIndex++;
      }

      result.push(sensor);
    }

    return result.sort((a, b) => (a.order > b.order ? 1 : -1));
  }

  private localizeSensorTypes(): void {
    if (!this.sensorTypes) {
      return;
    }
    for (const type of this.sensorTypes) {
      if (type?.value === 'TE') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.AMBIENT_TEMP');
      } else if (type?.value === 'FL') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.FLOW');
      } else if (type?.value === 'HU') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.HUMIDITY');
      } else if (type?.value === 'SH') {
        type.name = this.translate.instant(
          'STATICS.SENSOR_TYPES.SOIL_HUMIDITY'
        );
      } else if (type?.value === 'SC') {
        type.name = this.translate.instant(
          'STATICS.SENSOR_TYPES.CONDUCTIVITY'
        );
      } else if (type?.value === 'HH') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.LEAF_HUMIDITY');
      } else if (type?.value === 'E8') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.ENVIROPRO_8');
      } else if (type?.value === 'DE') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.DENDROMETER');
      } else if (type?.value === 'ED') {
        type.name = this.translate.instant('SensorType_ED');
      } else if (type?.value === 'DIGITAL') {
        type.name = this.translate.instant('SensorType_DIGITAL');
      } else if (type?.value === 'PL') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.PLUVIOMETER');
      } else if (type?.value === 'BA') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.BATTERY');
      } else if (type?.value === 'AN') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.ANEMOMETER');
      } else if (type?.value === 'CO' || type?.value === 'WC') {
        type.name = this.translate.instant('STATICS.SENSOR_TYPES.CONDUCTIVITY');
      }
    }
  }

  private refreshSensorNames(): void {
    if (!this.sensores || !this.sensorTypes) {
      return;
    }
    this.sensorNames = this.sensores.map(sensor => {
      const found = this.sensorTypes.find(t => t.value === sensor.sensorType);
      return found ? found.name : sensor.sensorType;
    });
  }

  private isDeletedEntity(entity: any): boolean {
    return !!entity && (
      entity.isDeleted === true ||
      String(entity.isDeleted) === 'true'
    );
  }

  private seriesLabel(
    key: string,
    moduleName?: string,
    includeModuleName = false
  ): string {
    const baseLabel = this.translate.instant(key);
    return includeModuleName && moduleName
      ? `${baseLabel} · ${moduleName}`
      : baseLabel;
  }

  private refreshSeriesTranslations(): void {
    const charts = [this.chart1, this.chart2];
    for (const chart of charts) {
      if (!chart) {
        continue;
      }
      for (const series of chart.series) {
        const key = (series as any).i18nKey;
        if (key) {
          (series as any).update(
            {
              name: this.seriesLabel(
                key,
                (series as any).moduleName,
                !!(series as any).includeModuleName
              )
            },
            false
          );
        }
      }
      chart.redraw();
    }
  }

  private applyGlobalHighchartsOptions(): void {
    const lang = this.getHighchartsLang();
    const theme = this.getHighchartsTheme();
    Highcharts.setOptions({
      exporting: {
        enabled: true
      },
      navigation: {
        buttonOptions: {
          enabled: true
        }
      },
      time: {
        useUTC: false
      } as any,
      lang: lang as any,
      ...theme
    });
  }

  private getHighchartsLang(): any {
    const locale = this.getHighchartsLocale();
    return {
      locale,
      accessibility: {
        zoom: {
          mapZoomIn: this.translate.instant('STATICS.HIGHCHARTS.MAP_ZOOM_IN')
        }
      },
      months: this.translate.instant('STATICS.HIGHCHARTS.MONTHS'),
      shortMonths: this.translate.instant('STATICS.HIGHCHARTS.SHORT_MONTHS'),
      weekdays: this.translate.instant('STATICS.HIGHCHARTS.WEEKDAYS'),
      loading: this.translate.instant('STATICS.HIGHCHARTS.LOADING'),
      viewFullscreen: this.translate.instant(
        'STATICS.HIGHCHARTS.VIEW_FULLSCREEN'
      ),
      noData: this.translate.instant('STATICS.HIGHCHARTS.NO_DATA'),
      resetZoom: this.translate.instant('STATICS.HIGHCHARTS.RESET_ZOOM'),
      printChart: this.translate.instant('STATICS.HIGHCHARTS.PRINT'),
      downloadJPEG: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_JPEG'),
      downloadPDF: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_PDF'),
      downloadPNG: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_PNG'),
      downloadSVG: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_SVG'),
      downloadCSV: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_CSV'),
      downloadXLS: this.translate.instant('STATICS.HIGHCHARTS.DOWNLOAD_XLS'),
      rangeSelectorZoom: this.translate.instant(
        'STATICS.HIGHCHARTS.RANGE_ZOOM'
      ),
      rangeSelectorFrom: this.translate.instant(
        'STATICS.HIGHCHARTS.RANGE_FROM'
      ),
      rangeSelectorTo: this.translate.instant('STATICS.HIGHCHARTS.RANGE_TO')
    };
  }

  private getHighchartsLocale(): string {
    const lang = this.translate.currentLang || 'en';
    if (lang.startsWith('es')) {
      return 'es-ES';
    }
    if (lang.startsWith('fr')) {
      return 'fr-FR';
    }
    return 'en-US';
  }

  private getWindDirectionLabels(): string[] {
    const dirs = this.translate.instant('STATICS.WIND.CARDINALS');
    return Array.isArray(dirs) ? dirs : [];
  }

  private getCssVar(name: string, fallback: string): string {
    if (typeof document === 'undefined') {
      return fallback;
    }
    const value = getComputedStyle(document.body)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  }

  private getHighchartsTheme(): any {
    const textColor = this.getCssVar('--app-text', '#1f2937');
    const mutedColor = this.getCssVar('--app-muted', '#6b7280');
    const surface = this.getCssVar('--app-surface', '#ffffff');
    const surfaceAlt = this.getCssVar('--app-surface-2', '#f8fafc');
    const border = this.getCssVar('--app-border', '#e5e7eb');
    const borderStrong = this.getCssVar('--app-border-strong', '#d1d5db');

    return {
      chart: {
        backgroundColor: surface,
        style: {
          fontFamily: 'Open Sans, sans-serif',
          color: textColor
        },
        plotBorderColor: border
      },
      title: {
        style: { color: textColor }
      },
      subtitle: {
        style: { color: mutedColor }
      },
      xAxis: {
        gridLineColor: border,
        lineColor: border,
        tickColor: border,
        labels: {
          style: { color: mutedColor }
        }
      },
      yAxis: {
        gridLineColor: border,
        lineColor: border,
        tickColor: border,
        labels: {
          style: { color: mutedColor }
        },
        title: {
          style: { color: mutedColor }
        }
      },
      legend: {
        itemStyle: { color: textColor },
        itemHoverStyle: { color: textColor },
        itemHiddenStyle: { color: mutedColor }
      },
      tooltip: {
        backgroundColor: surface,
        borderColor: border,
        style: { color: textColor }
      },
      rangeSelector: {
        buttonTheme: {
          fill: surfaceAlt,
          stroke: border,
          style: { color: textColor },
          states: {
            hover: {
              fill: borderStrong,
              style: { color: textColor }
            },
            select: {
              fill: border,
              style: { color: textColor }
            }
          }
        },
        inputStyle: {
          backgroundColor: surface,
          color: textColor
        },
        labelStyle: {
          color: mutedColor
        }
      },
      navigator: {
        outlineColor: border,
        xAxis: {
          gridLineColor: border,
          labels: {
            style: { color: mutedColor }
          }
        },
        handles: {
          backgroundColor: surfaceAlt,
          borderColor: borderStrong
        }
      },
      scrollbar: {
        barBackgroundColor: border,
        barBorderColor: border,
        buttonArrowColor: mutedColor,
        buttonBackgroundColor: surface,
        buttonBorderColor: border,
        rifleColor: mutedColor,
        trackBackgroundColor: surface,
        trackBorderColor: border
      }
    };
  }

  private applyThemeToOptions(options: Highcharts.Options, theme: any): void {
    if (!options || !theme) {
      return;
    }

    options.chart = {
      ...(options.chart || {}),
      ...(theme.chart || {}),
      backgroundColor:
        theme.chart?.backgroundColor ||
        options.chart?.backgroundColor ||
        'transparent'
    } as any;

    const xAxis = options.xAxis as any;
    if (xAxis) {
      options.xAxis = {
        ...xAxis,
        ...theme.xAxis,
        labels: {
          ...(xAxis.labels || {}),
          ...(theme.xAxis?.labels || {})
        }
      } as any;
    }

    if (Array.isArray(options.yAxis)) {
      options.yAxis = options.yAxis.map(axis => ({
        ...axis,
        gridLineColor:
          theme.yAxis?.gridLineColor ?? (axis as any).gridLineColor,
        lineColor: theme.yAxis?.lineColor ?? (axis as any).lineColor,
        tickColor: theme.yAxis?.tickColor ?? (axis as any).tickColor,
        labels: {
          ...(axis as any).labels,
          ...(theme.yAxis?.labels || {})
        },
        title: {
          ...(axis as any).title,
          ...(theme.yAxis?.title || {})
        }
      }));
    }

    options.legend = {
      ...(options.legend || {}),
      ...(theme.legend || {})
    };

    options.tooltip = {
      ...(options.tooltip || {}),
      ...(theme.tooltip || {}),
      borderColor: this.getCssVar('--app-border-strong', '#ffffff66'),
      borderWidth: 2
    } as any;

    options.rangeSelector = {
      ...(options.rangeSelector || {}),
      ...(theme.rangeSelector || {})
    } as any;

    options.navigator = {
      ...(options.navigator || {}),
      ...(theme.navigator || {})
    } as any;

    options.scrollbar = {
      ...(options.scrollbar || {}),
      ...(theme.scrollbar || {})
    } as any;
  }

  private applyThemeToChart(chart: Highcharts.Chart | undefined): void {
    if (!chart) {
      return;
    }
    const theme = this.getHighchartsTheme();
    chart.update(
      {
        chart: {
          ...(chart.options.chart || {}),
          ...(theme.chart || {})
        },
        xAxis: theme.xAxis,
        legend: theme.legend,
        tooltip: {
          ...(chart.options.tooltip || {}),
          ...(theme.tooltip || {}),
          borderColor: this.getCssVar('--app-border-strong', '#ffffff66'),
          borderWidth: 2,
          backgroundColor:
            theme.tooltip?.backgroundColor ||
            chart.options.tooltip?.backgroundColor ||
            this.getCssVar('--app-surface', '#0f172a')
        },
        rangeSelector: theme.rangeSelector,
        navigator: theme.navigator,
        scrollbar: theme.scrollbar
      },
      false
    );

    if (theme.yAxis) {
      chart.yAxis.forEach(axis =>
        axis.update(
          {
            gridLineColor: theme.yAxis.gridLineColor,
            lineColor: theme.yAxis.lineColor,
            tickColor: theme.yAxis.tickColor,
            labels: {
              style: theme.yAxis.labels?.style
            },
            title: {
              style: theme.yAxis.title?.style
            }
          },
          false
        )
      );
    }

    chart.redraw();
  }

  private applyRangeSelectorLabels(options: Highcharts.Options): void {
    const rangeLabels = [
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_1M'),
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_3M'),
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_6M'),
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_YTD'),
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_1Y'),
      this.translate.instant('STATICS.HIGHCHARTS.RANGE_ALL')
    ];
    if (options?.rangeSelector?.buttons) {
      options.rangeSelector = {
        ...options.rangeSelector,
        allButtonsEnabled: false,
        buttons: options.rangeSelector.buttons.map((btn, idx) => ({
          ...btn,
          text: rangeLabels[idx] ?? (btn as any).text
        }))
      } as any;
    }
  }

  private updateAxisColors(chart: Highcharts.Chart | undefined): void {
    if (!chart) {
      return;
    }
    chart.yAxis.forEach(axis => {
      const serie = chart.series.find(s => s.yAxis === axis);
      const color = (serie as any)?.color || (serie as any)?.options?.color;
      if (color) {
        axis.update(
          {
            lineColor: color,
            tickColor: color,
            labels: {
              ...(axis.options.labels || {}),
              enabled: true,
              reserveSpace: true,
              style: {
                ...(axis.options.labels?.style || {}),
                color
              }
            },
            title: {
              ...(axis.options.title || {}),
              style: {
                ...(axis.options.title?.style || {}),
                color
              }
            }
          },
          false
        );
      }
    });
    chart.redraw();
  }

  syncExtremes(e: any): void {
    if (!e || e.trigger === 'syncExtremes') {
      return;
    }

    const sourceChart: Highcharts.Chart = e.target.chart;
    const charts: Array<{ key: ChartKey; chart?: Highcharts.Chart }> = [
      { key: 'chart1', chart: this.chart1 },
      { key: 'chart2', chart: this.chart2 }
    ];

    for (const entry of charts) {
      if (!entry.chart || entry.chart === sourceChart) {
        continue;
      }
      if (entry.chart.xAxis[0].setExtremes) {
        entry.chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
          trigger: 'syncExtremes'
        });
      }
    }
  }

  updateRangeControls(
    chart: Highcharts.Chart | undefined,
    enableRangeSelector: boolean,
    enableNavigator: boolean
  ): void {
    if (!chart?.container) {
      return;
    }
    if (this.isMobileViewport()) {
      enableRangeSelector = false;
      enableNavigator = false;
    }
    const wrapper = chart.container.parentElement;
    if (!wrapper) {
      return;
    }
    chart.update(
      {
        rangeSelector: {
          enabled: enableRangeSelector
        },
        navigator: {
          enabled: enableNavigator
        }
      },
      true,
      true
    );
    wrapper.classList.remove('has-range-controls', 'no-range-controls');
    wrapper.classList.add(
      enableRangeSelector ? 'has-range-controls' : 'no-range-controls'
    );
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' ? window.innerWidth < 900 : false;
  }

  get humidityDepthOptions(): string[] {
    if (this.hasSoilHumidity && !this.hasEnviropro8) {
      return ['hum'];
    }

    const baseOptions = this.hasEnviropro8
      ? ['H10', 'H20', 'H30', 'H40', 'H50', 'H60', 'H70', 'H80']
      : ['H10', 'H20', 'H30', 'H40', 'H50', 'H60'];

    return this.hasSoilHumidity ? ['hum', ...baseOptions] : baseOptions;
  }

  get conductivityDepthOptions(): string[] {
    return ['C10', 'C20', 'C30', 'C40', 'C50', 'C60', 'C70', 'C80'];
  }

  private isHumiditySensorType(sensorType: SensorType): boolean {
    return (
      sensorType === 'HU' ||
      sensorType === 'E8' ||
      sensorType === 'SH' ||
      sensorType === 'SC'
    );
  }

  private normalizeHumidityGraphName(
    sensorType: SensorType,
    graphName: string
  ): string {
    if (this.isHumiditySensorType(sensorType)) {
      const prefix = `${sensorType}_`;
      if (graphName.startsWith(prefix)) {
        return graphName.substring(prefix.length);
      }
    }
    return graphName;
  }

  private transformEnviroproPayload(
    sensorType: SensorType,
    sensorData: any
  ): any {
    if (!sensorData || typeof sensorData !== 'object') {
      return sensorData;
    }

    if (sensorType === 'SH' || sensorType === 'SC') {
      const transformed: any = {};
      for (const [key, value] of Object.entries(sensorData) as Array<
        [string, any]
      >) {
        if (key === 'all') {
          continue;
        }
        if (sensorType === 'SC' && /^SC_H\d{1,2}$/.test(key)) {
          continue;
        }
        transformed[key] = value;
      }
      if (sensorData.all && typeof sensorData.all === 'object') {
        const entries = Object.entries(sensorData.all) as Array<[string, any]>;
        for (const [key, value] of entries) {
          if (sensorType === 'SH' && key === 'SH_H10' && sensorData.all.SH_hum) {
            continue;
          }
          if (sensorType === 'SC' && /^SC_H\d{1,2}$/.test(key)) {
            continue;
          }
          transformed[key] = value;
        }
        delete transformed.all;
      }
      return transformed;
    }

    if (sensorType !== 'E8') {
      return sensorData;
    }

    const transformed: any = {};
    const mapDepth = ['0', '10', '20', '30', '40', '50', '60', '70', '80'];
    const normalizeE8Key = (key: string): string => {
      const hMatch = key.match(/^E8_H([1-8])$/);
      if (hMatch) {
        const idx = parseInt(hMatch[1], 10);
        return `E8_H${mapDepth[idx]}`;
      }

      const cMatch = key.match(/^E8_C([1-8])$/);
      if (cMatch) {
        const idx = parseInt(cMatch[1], 10);
        return `E8_C${mapDepth[idx]}`;
      }

      return key;
    };

    const topLevelEntries = Object.entries(sensorData) as Array<[string, any]>;
    for (const [key, value] of topLevelEntries) {
      if (key === 'all') {
        continue;
      }
      transformed[normalizeE8Key(key)] = value;
    }

    if (sensorData.all && typeof sensorData.all === 'object') {
      const entries = Object.entries(sensorData.all) as Array<[string, any]>;
      for (const [key, value] of entries) {
        transformed[normalizeE8Key(key)] = value;
      }
    }
    return transformed;
  }

  getHumidityOptionLabel(option: string): string {
    return option === 'hum'
      ? this.translate.instant('STATICS.SERIES.SOIL_MOISTURE')
      : option;
  }

  private getHumiditySelectionKey(
    sensorType: SensorType,
    normalizedGraphName: string
  ): string | null {
    if (!this.isHumiditySensorType(sensorType)) {
      return null;
    }

    if (
      sensorType === 'SH' &&
      ['hum', 'SH_hum', 'H10'].includes(normalizedGraphName)
    ) {
      return 'hum';
    }

    if (
      sensorType === 'SC' &&
      ['hum', 'SC_hum'].includes(normalizedGraphName)
    ) {
      return 'hum';
    }

    if (sensorType === 'SC') {
      return null;
    }

    return normalizedGraphName.match(/^H\d{2}$/) ? normalizedGraphName : null;
  }

  private stringDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + (date.getDate() + 1)).slice(-2);
    const stringdate = year + month + day + 'T235959+00:00';
    return stringdate;
  }

  private stringStartDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const stringdate = year + month + day + 'T000000+00:00';
    return stringdate;
  }

  private getZonesAtDate(): string {
    const baseDate = this.dateRange?.start ?? new Date();
    const year = baseDate.getFullYear();
    const month = ('0' + (baseDate.getMonth() + 1)).slice(-2);
    const day = ('0' + baseDate.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private getSeriesMeta(
    sensorType: SensorType,
    graphName: string
  ): SeriesMeta | null {
    const humidityGraphName = this.isHumiditySensorType(sensorType)
      ? this.normalizeHumidityGraphName(sensorType, graphName)
      : graphName;

    if (this.isHumiditySensorType(sensorType)) {
      if (
        sensorType === 'SC' &&
        ['hum', 'SC_hum'].includes(humidityGraphName)
      ) {
        return {
          nameKey: 'STATICS.SERIES.SOIL_MOISTURE',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (
        sensorType === 'SC' &&
        ['ec', 'cond', 'SC_ec', 'SC_cond'].includes(humidityGraphName)
      ) {
        return {
          nameKey: 'STATICS.SERIES.SOIL_ELECTRICAL_CONDUCTIVITY',
          group: 'soil-conductivity',
          decimals: 2,
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (
        sensorType === 'SC' &&
        ['temp', 'SC_temp'].includes(humidityGraphName)
      ) {
        return {
          nameKey: 'STATICS.SERIES.SOIL_TEMP',
          group: 'soil-temperature',
          decimals: 1,
          suffix: '\u00b0C',
          unit: '\u00b0C'
        };
      }
      if (sensorType === 'SC' && /^H\d{1,2}$/.test(humidityGraphName)) {
        return null;
      }
      if (
        sensorType === 'SH' &&
        ['hum', 'SH_hum', 'H10'].includes(humidityGraphName)
      ) {
        return {
          nameKey: 'STATICS.SERIES.SOIL_MOISTURE',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (sensorType === 'SH' && humidityGraphName === 'temp') {
        return {
          nameKey: 'STATICS.SERIES.SOIL_TEMP',
          group: 'soil-temperature',
          decimals: 1,
          suffix: '\u00b0C',
          unit: '\u00b0C'
        };
      }
      if (humidityGraphName === 'H10') {
        return {
          nameKey: 'STATICS.SERIES.H10',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H20') {
        return {
          nameKey: 'STATICS.SERIES.H20',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H30') {
        return {
          nameKey: 'STATICS.SERIES.H30',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H40') {
        return {
          nameKey: 'STATICS.SERIES.H40',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H50') {
        return {
          nameKey: 'STATICS.SERIES.H50',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H60') {
        return {
          nameKey: 'STATICS.SERIES.H60',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H70') {
        return {
          nameKey: 'STATICS.SERIES.H70',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'H80') {
        return {
          nameKey: 'STATICS.SERIES.H80',
          group: 'humidity-depth',
          decimals: 1
        };
      }
      if (humidityGraphName === 'C10') {
        return {
          nameKey: 'STATICS.SERIES.C10',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C20') {
        return {
          nameKey: 'STATICS.SERIES.C20',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C30') {
        return {
          nameKey: 'STATICS.SERIES.C30',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C40') {
        return {
          nameKey: 'STATICS.SERIES.C40',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C50') {
        return {
          nameKey: 'STATICS.SERIES.C50',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C60') {
        return {
          nameKey: 'STATICS.SERIES.C60',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C70') {
        return {
          nameKey: 'STATICS.SERIES.C70',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'C80') {
        return {
          nameKey: 'STATICS.SERIES.C80',
          group: 'conductivity-depth',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (humidityGraphName === 'sumatoryControl') {
        return {
          nameKey: 'STATICS.SERIES.CONTROL',
          group: 'humidity-control',
          decimals: 1,
          applyZones: true
        };
      }
      if (humidityGraphName === 'sumatory') {
        return { nameKey: 'STATICS.SERIES.SUMMARY', group: 'humidity-summary' };
      }
      if (humidityGraphName === 'Hu_sewerSystemType') {
        return {
          nameKey: 'STATICS.SERIES.DRAINAGE',
          group: 'humidity-drainage',
          decimals: 1
        };
      }
      if (humidityGraphName === 'Hu_lts') {
        return {
          nameKey: 'STATICS.SERIES.LTS',
          group: 'humidity-lts',
          type: 'column'
        };
      }
      if (humidityGraphName === 'Hu_lti') {
        return {
          nameKey: 'STATICS.SERIES.LTI',
          group: 'humidity-lti',
          type: 'column'
        };
      }
    }

    if (sensorType === 'FL') {
      if (graphName === 'FL_c_data') {
        return {
          nameKey: 'STATICS.SERIES.FLOW',
          group: 'flow',
          type: 'column',
          suffix: 'l',
          unit: 'l'
        };
      }
      if (graphName === 'FL_c_processed') {
        return {
          nameKey: 'STATICS.SERIES.DAILY_IRRIGATION',
          group: 'flow-processed',
          type: 'column',
          suffix: 'l',
          unit: 'l'
        };
      }
    }

    if (sensorType === 'TE') {
      if (graphName === 'TE_hr') {
        return {
          nameKey: 'STATICS.SERIES.RELATIVE_HUMIDITY',
          group: 'ambient-humidity',
          decimals: 1,
          suffix: '%',
          unit: '%'
        };
      }
      if (graphName === 'TE_temp') {
        return {
          nameKey: 'STATICS.SERIES.AMBIENT_TEMP',
          group: 'ambient-temp',
          decimals: 1,
          suffix: '\u00b0C',
          unit: '\u00b0C'
        };
      }
      return null;
    }

    if (sensorType === 'HH') {
      if (graphName === 'HH_hum') {
        return {
          nameKey: 'STATICS.SERIES.LEAF_HUMIDITY',
          group: 'leaf-humidity',
          decimals: 1,
          suffix: '%',
          unit: '%'
        };
      }
      if (graphName === 'HH_temp') {
        return {
          nameKey: 'STATICS.SERIES.LEAF_TEMP',
          group: 'leaf-temp',
          decimals: 1,
          suffix: '\u00b0C',
          unit: '\u00b0C'
        };
      }
    }

    if (sensorType === 'CO') {
      if (graphName === 'CO_cond') {
        return {
          nameKey: 'STATICS.SERIES.CONDUCTIVITY',
          group: 'conductivity',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
      if (graphName === 'CO_perm') {
        return {
          nameKey: 'STATICS.SERIES.PERMITTIVITY',
          group: 'permittivity'
        };
      }
      if (graphName === 'CO_temp') {
        return {
          nameKey: 'STATICS.SERIES.SOIL_TEMP',
          group: 'soil-temp',
          decimals: 1,
          suffix: '\u00b0C',
          unit: '\u00b0C'
        };
      }
      if (graphName === 'CO_solution') {
        return {
          nameKey: 'STATICS.SERIES.SOLUTION_CONDUCTIVITY',
          group: 'solution-conductivity',
          suffix: 'dS/m',
          unit: 'dS/m'
        };
      }
    }

    if (sensorType === 'DE') {
      if (graphName === 'DE_d') {
        return { nameKey: 'STATICS.SERIES.DENDROMETER', group: 'dendrometer' };
      }
      if (graphName === 'contraction_d') {
        return {
          nameKey: 'STATICS.SERIES.CONTRACTION',
          group: 'dendro-contraction',
          type: 'column'
        };
      }
      if (graphName === 'dilatation_d') {
        return { nameKey: 'STATICS.SERIES.GROWTH', group: 'dendro-growth' };
      }
    }

    if (sensorType === 'ED' || sensorType === 'DIGITAL') {
      if (graphName === 'ED_ed' || graphName === 'DIGITAL_ed') {
        return { nameKey: 'STATICS.SERIES.DIGITAL', group: 'digital' };
      }
    }

    if (sensorType === 'WC') {
      return {
        nameKey: 'STATICS.SERIES.IRRIGATION_CONDUCTIVITY',
        group: 'irrigation-conductivity',
        suffix: 'dS/m',
        unit: 'dS/m'
      };
    }

    if (sensorType === 'BA') {
      return {
        nameKey: 'STATICS.SERIES.BATTERY',
        group: 'battery',
        suffix: '%',
        unit: '%'
      };
    }

    if (sensorType === 'PL') {
      return {
        nameKey: 'STATICS.SERIES.PRECIPITATION',
        group: 'precipitation',
        type: 'column',
        suffix: 'l',
        unit: 'l'
      };
    }

    if (sensorType === 'AN') {
      if (graphName === 'AN_vv') {
        return {
          nameKey: 'STATICS.SERIES.WIND_SPEED',
          group: 'wind-speed',
          suffix: 'km/h',
          unit: 'km/h'
        };
      }
      if (graphName === 'AN_dv') {
        return {
          nameKey: 'STATICS.SERIES.WIND_DIRECTION',
          group: 'wind-direction',
          suffix: '\u00b0',
          unit: '\u00b0'
        };
      }
    }

    return { nameKey: graphName, group: `${sensorType}-${graphName}` };
  }
}
